import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  Loader2, RefreshCw, ChevronLeft, AlertTriangle, Clock, HelpCircle,
  CheckCircle2, PenLine, Plus, Send,
} from 'lucide-react';
import DeliveryBadge from './DeliveryBadge';

const API = '/api';

// LEAD FILES — one page per person: where they stand, what has actually happened, and what
// to do next. The list sorts by what is OWED, not alphabetically: overdue actions first,
// then longest silence, because that is the order Randall needs to work in.

const STAGES = ['new', 'contacted', 'intake_received', 'call_booked', 'call_held',
  'agreement_sent', 'signed', 'not_now', 'declined'];
const STAGE_LABEL = {
  new: 'New', contacted: 'Contacted', intake_received: 'Intake received',
  call_booked: 'Call booked', call_held: 'Call held', agreement_sent: 'Agreement sent',
  signed: 'Signed', not_now: 'Not now', declined: 'Declined',
};

const day = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return String(iso).slice(0, 10); }
};
const isOverdue = (d) => !!d && new Date(d) < new Date(new Date().toDateString());

function Section({ n, title, children, right }) {
  return (
    <section className="rounded-lg border border-hg-border bg-hg-panel p-4 mb-4">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-sm font-semibold text-hg-text">
          <span className="text-hg-text-dim mr-2">{n}</span>{title}
        </h3>
        {right}
      </div>
      {children}
    </section>
  );
}

export default function LeadFiles({ onWrite }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [f, setF] = useState({ stage: '', action: '', overdue: false, questions: false, source: '' });

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const r = await axios.get(`${API}/lead-files`, { params: { limit: 500 } });
      setRows(r.data.rows || []);
    } catch (e) { setErr(e?.response?.data?.detail || e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  if (openId) return <LeadFileDetail id={openId} onWrite={onWrite}
    onBack={() => { setOpenId(null); load(); }} />;

  let list = rows.filter((r) => {
    if (f.stage && r.stage !== f.stage) return false;
    if (f.action === 'has' && !r.next_action) return false;
    if (f.action === 'none' && r.next_action) return false;
    if (f.overdue && !isOverdue(r.next_action?.due_on)) return false;
    if (f.questions && !r.open_questions) return false;
    if (f.source && !(r.source || '').toLowerCase().includes(f.source.toLowerCase())) return false;
    return true;
  });
  // DEFAULT SORT: overdue next actions first, then longest silence. What is owed, first.
  list = [...list].sort((a, b) => {
    const ao = isOverdue(a.next_action?.due_on) ? 0 : 1;
    const bo = isOverdue(b.next_action?.due_on) ? 0 : 1;
    if (ao !== bo) return ao - bo;
    if (ao === 0) return String(a.next_action?.due_on).localeCompare(String(b.next_action?.due_on));
    return (b.days_in_stage ?? -1) - (a.days_in_stage ?? -1);
  });

  return (
    <div data-testid="lead-files">
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        <select data-testid="lf-filter-stage" value={f.stage} onChange={(e) => setF({ ...f, stage: e.target.value })}
          className="bg-hg-panel border border-hg-border rounded px-2 py-1 text-sm text-hg-text">
          <option value="">All stages</option>
          {STAGES.map((s) => <option key={s} value={s}>{STAGE_LABEL[s]}</option>)}
        </select>
        <select value={f.action} onChange={(e) => setF({ ...f, action: e.target.value })}
          className="bg-hg-panel border border-hg-border rounded px-2 py-1 text-sm text-hg-text">
          <option value="">Any next action</option>
          <option value="has">Has a next action</option>
          <option value="none">No next action set</option>
        </select>
        <input placeholder="source contains…" value={f.source}
          onChange={(e) => setF({ ...f, source: e.target.value })}
          className="bg-hg-panel border border-hg-border rounded px-2 py-1 text-sm text-hg-text" />
        <label className="text-sm text-hg-text-dim flex items-center gap-1">
          <input type="checkbox" checked={f.overdue} onChange={(e) => setF({ ...f, overdue: e.target.checked })} />
          Overdue only
        </label>
        <label className="text-sm text-hg-text-dim flex items-center gap-1">
          <input type="checkbox" checked={f.questions} onChange={(e) => setF({ ...f, questions: e.target.checked })} />
          Has open questions
        </label>
        <button onClick={load} className="ml-auto text-hg-text-dim hover:text-hg-text" title="Refresh">
          <RefreshCw size={16} />
        </button>
      </div>

      {err && <div className="text-red-300 text-sm mb-3">{err}</div>}
      {loading ? <Loader2 className="animate-spin text-hg-text-dim" />
        : (
          <div className="overflow-x-auto rounded-lg border border-hg-border">
            <table className="w-full text-sm">
              <thead className="bg-hg-panel text-hg-text-dim">
                <tr>
                  {['Name', 'Stage', 'Days in stage', 'Silence', 'Next action', 'Due', 'Open Qs'].map((h) => (
                    <th key={h} className="text-left px-3 py-2 font-medium whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {list.map((r) => (
                  <tr key={r.id} data-testid="lf-row" onClick={() => setOpenId(r.id)}
                    className="border-t border-hg-border hover:bg-hg-panel/60 cursor-pointer">
                    <td className="px-3 py-2">
                      <div className="text-hg-text">{r.name || <span className="text-hg-text-dim italic">no name</span>}</div>
                      <div className="text-hg-text-dim text-xs">{r.email}</div>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">{STAGE_LABEL[r.stage] || r.stage}</td>
                    <td className="px-3 py-2">{r.days_in_stage ?? '—'}</td>
                    <td className="px-3 py-2">{r.days_since_last_contact ?? '—'}</td>
                    <td className="px-3 py-2">
                      {r.next_action?.what || <span className="text-hg-text-dim italic">none set</span>}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap">
                      {r.next_action?.due_on
                        ? <span className={isOverdue(r.next_action.due_on) ? 'text-red-300 font-medium' : ''}>
                          {isOverdue(r.next_action.due_on) && <AlertTriangle size={12} className="inline mr-1" />}
                          {day(r.next_action.due_on)}
                        </span>
                        : '—'}
                    </td>
                    <td className="px-3 py-2">{r.open_questions || ''}</td>
                  </tr>
                ))}
                {!list.length && (
                  <tr><td colSpan={7} className="px-3 py-6 text-center text-hg-text-dim">No files match these filters.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      <p className="text-xs text-hg-text-dim mt-2">{list.length} of {rows.length} files. Sorted by what is owed: overdue first, then longest silence.</p>
    </div>
  );
}

export function LeadFileDetail({ id, onBack, onWrite }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [act, setAct] = useState({ what: '', due_on: '' });
  const [q, setQ] = useState({ question: '', owed_by: 'us' });

  const load = useCallback(async () => {
    try { setD((await axios.get(`${API}/lead-files/${id}`)).data); setErr(null); }
    catch (e) { setErr(e?.response?.data?.detail || e.message); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const post = async (path, body) => {
    setBusy(true); setErr(null);
    try { setD((await axios.post(`${API}/lead-files/${id}${path}`, body)).data); }
    catch (e) { setErr(e?.response?.data?.detail || e.message); }
    setBusy(false);
  };

  if (err && !d) return <div className="text-red-300 text-sm">{err} <button onClick={onBack} className="underline">back</button></div>;
  if (!d) return <Loader2 className="animate-spin text-hg-text-dim" />;

  const w = d.who, s = d.where_they_stand, a = d.automation;

  return (
    <div data-testid="lead-file-detail">
      <div className="flex items-center justify-between mb-3">
        <button onClick={onBack} className="flex items-center gap-1 text-hg-text-dim hover:text-hg-text text-sm">
          <ChevronLeft size={16} /> All lead files
        </button>
        {onWrite && (
          <button data-testid="lf-write" onClick={() => onWrite(d.who.email)}
            className="text-sm border border-hg-green/50 text-hg-green rounded px-3 py-1 hover:bg-hg-green/10">
            <Send size={14} className="inline mr-1" />Write to them
          </button>
        )}
      </div>
      {err && <div className="text-red-300 text-sm mb-3">{err}</div>}

      <Section n="1" title="Who">
        <div className="grid sm:grid-cols-2 gap-y-1 text-sm">
          <div><span className="text-hg-text-dim">Name </span>{w.name || <em className="text-hg-text-dim">unknown</em>}</div>
          <div><span className="text-hg-text-dim">Email </span>{w.email}</div>
          <div><span className="text-hg-text-dim">Phone </span>{w.phone || '—'}</div>
          <div><span className="text-hg-text-dim">Location </span>{w.location || '—'}</div>
          <div><span className="text-hg-text-dim">Source </span>{w.source || '—'}</div>
          <div><span className="text-hg-text-dim">Arrived </span>{day(w.arrived_at)}</div>
        </div>
        {!w.has_lead_record && (
          <p className="mt-2 text-xs text-hg-text-dim border-l-2 border-hg-border pl-2">
            No lead record exists for this person — this file is the only record the Hub holds.
          </p>
        )}
      </Section>

      <Section n="2" title="Where they stand"
        right={<span className="text-xs text-hg-text-dim">{s.days_in_stage ?? '—'}d in stage · {s.days_since_last_contact ?? '—'}d since any contact</span>}>
        <div className="flex items-center gap-2 flex-wrap">
          <select data-testid="lf-stage" value={s.stage} disabled={busy}
            onChange={(e) => {
              const stage = e.target.value;
              const revisit = stage === 'not_now'
                ? window.prompt('Revisit date for "Not now" (YYYY-MM-DD):') : null;
              if (stage === 'not_now' && !revisit) return;
              post('/stage', { stage, revisit_on: revisit });
            }}
            className="bg-hg-panel border border-hg-border rounded px-2 py-1 text-sm text-hg-text">
            {STAGES.map((x) => <option key={x} value={x}>{STAGE_LABEL[x]}</option>)}
          </select>
          <span className="text-xs text-hg-text-dim">set by {s.by} on {day(s.since)}</span>
          {s.revisit_on && <span className="text-xs text-amber-300">revisit {day(s.revisit_on)}</span>}
        </div>
        {s.reply_flag_note && (
          <p data-testid="lf-reply-trap" className="mt-3 text-sm text-amber-300 border border-amber-400/40 bg-amber-400/10 rounded p-2">
            <AlertTriangle size={13} className="inline mr-1" />{s.reply_flag_note}
          </p>
        )}
        <details className="mt-3">
          <summary className="text-xs text-hg-text-dim cursor-pointer">Stage history ({s.history.length})</summary>
          <ul className="mt-1 text-xs text-hg-text-dim space-y-1">
            {s.history.map((h, i) => (
              <li key={i}>{day(h.at)} → <span className="text-hg-text">{STAGE_LABEL[h.stage] || h.stage}</span> by {h.by} ({h.how}){h.note ? ` — ${h.note}` : ''}</li>
            ))}
          </ul>
        </details>
      </Section>

      <Section n="3" title="What I need to do">
        {d.what_i_need_to_do.next_action ? (
          <div className="flex items-start gap-3">
            <div className="flex-1">
              <div className="text-hg-text">{d.what_i_need_to_do.next_action.what}</div>
              <div className={`text-xs ${isOverdue(d.what_i_need_to_do.next_action.due_on) ? 'text-red-300' : 'text-hg-text-dim'}`}>
                <Clock size={11} className="inline mr-1" />due {day(d.what_i_need_to_do.next_action.due_on)}
              </div>
            </div>
            <button data-testid="lf-complete-action" disabled={busy}
              onClick={() => post('/next-action/complete', { outcome: window.prompt('What happened? (optional)') || '' })}
              className="text-xs border border-hg-green/50 text-hg-green rounded px-2 py-1 hover:bg-hg-green/10">
              <CheckCircle2 size={12} className="inline mr-1" />Done
            </button>
          </div>
        ) : (
          <div className="flex gap-2 flex-wrap">
            <input placeholder="Next action…" value={act.what} onChange={(e) => setAct({ ...act, what: e.target.value })}
              className="flex-1 min-w-[200px] bg-hg-bg border border-hg-border rounded px-2 py-1 text-sm text-hg-text" />
            <input type="date" value={act.due_on} onChange={(e) => setAct({ ...act, due_on: e.target.value })}
              className="bg-hg-bg border border-hg-border rounded px-2 py-1 text-sm text-hg-text" />
            <button data-testid="lf-set-action" disabled={busy || !act.what.trim()}
              onClick={() => { post('/next-action', act); setAct({ what: '', due_on: '' }); }}
              className="text-xs border border-hg-border rounded px-2 py-1 text-hg-text hover:bg-hg-panel disabled:opacity-40">
              <Plus size={12} className="inline mr-1" />Set
            </button>
          </div>
        )}
        {!!d.what_i_need_to_do.completed.length && (
          <details className="mt-3">
            <summary className="text-xs text-hg-text-dim cursor-pointer">Completed ({d.what_i_need_to_do.completed.length})</summary>
            <ul className="mt-1 text-xs text-hg-text-dim space-y-1">
              {d.what_i_need_to_do.completed.map((c, i) => (
                <li key={i}>{day(c.completed_at)} — {c.what}{c.outcome ? ` → ${c.outcome}` : ''}</li>
              ))}
            </ul>
          </details>
        )}
      </Section>

      <Section n="4" title={`Open questions (${d.open_questions.open.length})`}>
        <ul className="space-y-2 mb-3">
          {d.open_questions.open.map((x) => (
            <li key={x.id} className="text-sm flex items-start gap-2">
              <span className={`text-[10px] uppercase rounded border px-1 py-0.5 mt-0.5 shrink-0 ${x.owed_by === 'us' ? 'text-amber-300 border-amber-400/50' : 'text-sky-300 border-sky-400/50'}`}>
                {x.owed_by === 'us' ? 'we owe' : 'they owe'}
              </span>
              <span className="flex-1 text-hg-text">{x.question}
                <span className="text-hg-text-dim text-xs"> · asked {day(x.asked_on)}</span>
              </span>
              <button disabled={busy}
                onClick={() => { const ans = window.prompt('Answer:'); if (ans) post(`/questions/${x.id}/resolve`, { answer: ans }); }}
                className="text-xs text-hg-text-dim hover:text-hg-green shrink-0">resolve</button>
            </li>
          ))}
          {!d.open_questions.open.length && <li className="text-sm text-hg-text-dim">None open.</li>}
        </ul>
        <div className="flex gap-2 flex-wrap">
          <input placeholder="New question…" value={q.question} onChange={(e) => setQ({ ...q, question: e.target.value })}
            className="flex-1 min-w-[200px] bg-hg-bg border border-hg-border rounded px-2 py-1 text-sm text-hg-text" />
          <select value={q.owed_by} onChange={(e) => setQ({ ...q, owed_by: e.target.value })}
            className="bg-hg-bg border border-hg-border rounded px-2 py-1 text-sm text-hg-text">
            <option value="us">We owe the answer</option>
            <option value="them">They owe the answer</option>
          </select>
          <button disabled={busy || !q.question.trim()}
            onClick={() => { post('/questions', q); setQ({ question: '', owed_by: 'us' }); }}
            className="text-xs border border-hg-border rounded px-2 py-1 text-hg-text hover:bg-hg-panel disabled:opacity-40">Add</button>
        </div>
      </Section>

      <Section n="5" title={`What's happened (${d.whats_happened.count})`}>
        <ul className="space-y-2">
          {d.whats_happened.events.map((e, i) => (
            <li key={i} className="text-sm flex items-start gap-2" data-testid="lf-event">
              <span className="text-hg-text-dim text-xs w-24 shrink-0 pt-0.5">{day(e.at)}</span>
              <DeliveryBadge state={e.delivery} />
              <span className="flex-1">
                <span className="text-hg-text">{e.title}</span>
                {e.detail && <span className="block text-xs text-hg-text-dim">{e.detail}</span>}
                {e.reason && <span className="block text-xs text-amber-300/80">{e.reason}</span>}
              </span>
            </li>
          ))}
          {!d.whats_happened.count && <li className="text-sm text-hg-text-dim">Nothing recorded.</li>}
        </ul>
      </Section>

      <Section n="6" title="Notes">
        <div className="flex gap-2 mb-3">
          <input placeholder="Add a note (append-only)…" value={note} onChange={(e) => setNote(e.target.value)}
            className="flex-1 bg-hg-bg border border-hg-border rounded px-2 py-1 text-sm text-hg-text" />
          <button data-testid="lf-add-note" disabled={busy || !note.trim()}
            onClick={() => { post('/notes', { text: note }); setNote(''); }}
            className="text-xs border border-hg-border rounded px-2 py-1 text-hg-text hover:bg-hg-panel disabled:opacity-40">Add</button>
        </div>
        <ul className="space-y-2">
          {[...d.notes.file_notes].reverse().map((n) => (
            <li key={n.id} className="text-sm">
              <span className="text-hg-text">{n.text}</span>
              <span className="block text-xs text-hg-text-dim">{day(n.at)} · {n.actor}</span>
            </li>
          ))}
          {d.notes.legacy.map((n, i) => (
            <li key={`l${i}`} className="text-sm border-l-2 border-hg-border pl-2">
              <span className="text-hg-text-dim">{n.text}</span>
              <span className="block text-xs text-hg-text-dim">{day(n.at)} · {n.actor} · <em>{n.origin}</em></span>
            </li>
          ))}
          {!d.notes.file_notes.length && !d.notes.legacy.length && <li className="text-sm text-hg-text-dim">No notes.</li>}
        </ul>
      </Section>

      <Section n="7" title="Automation state">
        <ul className="space-y-1 text-sm">
          {a.lines.map((l, i) => (
            <li key={i} className={l.includes('unknown') || l.includes('SUPPRESSION') ? 'text-amber-300' : 'text-hg-text'}>
              {(l.includes('unknown') || l.includes('SUPPRESSION')) && <HelpCircle size={12} className="inline mr-1" />}{l}
            </li>
          ))}
        </ul>
        <p className="mt-2 text-xs text-hg-text-dim">
          Subscriber status as the API reports it: <strong className="text-hg-text">{a.subscriber_status}</strong>
        </p>
      </Section>
    </div>
  );
}
